import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { isType } from 'variant';
import type { LooseAppManifest } from '@wix/cli-app-manifest';
import { useDebugLog } from '@wix/cli-debug-log';
import { useDevCenterOverridesClient } from '@wix/dev-center-overrides-client';
import { useTunnel } from '@wix/cli-tunnel';
import { readUserConfig } from '@wix/cli-user-config';
import type { DevMetadata } from '@wix/cli-astro-definitions';
import { ErrorViewer, useErrorReporter } from '@wix/cli-error-reporting';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { useExit } from '@wix/cli-ui-kit';
import { readAppConfig, saveAppConfig } from '../../../files/app.config.js';
import { useProjectModel } from '../../../model.js';
import { useAstroLogger } from '../utils/useAstroLogger.js';
import { useFetchAppManifest } from './useFetchAppManifest.js';
import { DevCenterWatcher } from '../utils/DevCenterWatcher.js';
import {
  checkManifestForChanges,
  ManifestUpdateResult,
} from './checkManifestForChanges.js';

const { pollInterval } = getTestOverrides();

export const useDevCenterUpdater = ({
  serverLocalUrl,
  serverExposedUrl,
  devMetadata,
}: {
  serverLocalUrl: string;
  serverExposedUrl?: string;
  devMetadata: DevMetadata | null;
}) => {
  const devCenterWatcher = useMemo(() => new DevCenterWatcher(), []);
  const [devArmTag, setDevArmTag] = useState<string | undefined>();
  const [manifest, setManifest] = useState<LooseAppManifest | null>(null);
  const { reportError } = useErrorReporter();
  const writeToDebugLog = useDebugLog();
  const exit = useExit();

  const {
    model: { projectFolder, releaseAppId },
  } = useProjectModel();

  const logger = useAstroLogger({ isPrefixed: true });
  const devCenterOverrides = useDevCenterOverridesClient();

  const openTunnel = useTunnel();
  const tunnelUrl = useRef<string>();
  const [tunnelRequired, setTunnelRequired] = useState(false);

  const fetchAppManifest = useFetchAppManifest();

  const baseUrl = serverExposedUrl ?? serverLocalUrl;

  const updateDevCenter = useCallback(
    async (latestManifest: LooseAppManifest, isTunnelRequired: boolean) => {
      const manifestUpdateResult = checkManifestForChanges(
        manifest,
        latestManifest
      );

      if (isType(manifestUpdateResult, 'Unchanged')) {
        return manifestUpdateResult;
      }

      if (!serverExposedUrl && !tunnelUrl.current && isTunnelRequired) {
        const config = await readUserConfig();
        if (!config.tunneling) {
          setTunnelRequired(true);
          return ManifestUpdateResult.Unchanged();
        }

        const serverLocalPort = parseInt(new URL(serverLocalUrl).port, 10);
        const tunnel = await openTunnel(serverLocalPort);
        tunnelUrl.current = tunnel.url;

        return ManifestUpdateResult.Unchanged();
      }

      const appConfig = await readAppConfig(projectFolder);

      let updatedDevArmTag = appConfig?.devArmTag;
      if (!updatedDevArmTag) {
        updatedDevArmTag = await devCenterOverrides.createLooseAppRelease(
          latestManifest,
          releaseAppId
        );
      } else {
        const updateState = await devCenterOverrides.updateLooseAppRelease(
          latestManifest,
          updatedDevArmTag
        );
        if (!isType(updateState, 'TagUpdated')) {
          updatedDevArmTag = await devCenterOverrides.createLooseAppRelease(
            latestManifest,
            releaseAppId
          );
        }
      }

      if (updatedDevArmTag !== appConfig?.devArmTag) {
        await saveAppConfig(projectFolder, { devArmTag: updatedDevArmTag });
      }

      setManifest(latestManifest);
      setDevArmTag(updatedDevArmTag);
      devCenterWatcher.emit('manifestUpdated', {
        armTag: updatedDevArmTag,
        updatedComponents: manifestUpdateResult.updatedComponents,
      });

      return manifestUpdateResult;
    },
    [
      releaseAppId,
      manifest,
      serverLocalUrl,
      serverExposedUrl,
      devCenterOverrides,
      projectFolder,
      openTunnel,
      devCenterWatcher,
    ]
  );

  const [timer, setTimer] = useState(Date.now());

  useEffect(() => {
    let disposed = false;

    async function fetchAndUpdate() {
      const { manifest, isTunnelRequired } = await fetchAppManifest(
        serverLocalUrl,
        devMetadata,
        baseUrl,
        tunnelUrl.current ?? baseUrl
      );

      try {
        const result = await updateDevCenter(manifest, isTunnelRequired);
        if (isType(result, 'Changed')) {
          logger.log(
            'App Manifest updated. Refresh existing browser tabs to view the latest changes.'
          );
        }
      } catch (error) {
        setManifest(manifest);

        logger.error(<ErrorViewer error={error} />);

        writeToDebugLog(error);
        reportError(error);

        throw error;
      }
    }

    void fetchAndUpdate()
      .catch((error: unknown) => {
        if (
          error instanceof Error &&
          error.name === 'TrustedAppIsNotMarkedAsInternal'
        ) {
          exit(error);
        }
      })
      .finally(() => {
        if (!disposed) {
          setTimeout(() => setTimer(Date.now()), pollInterval ?? 1_000);
        }
      });

    return () => {
      disposed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  return { baseUrl, tunnelRequired, devArmTag, devCenterWatcher };
};
